/**
 * Structured console logger + token masking.
 *
 * Extracted from apps/physio-app/src/utils/logger.ts (forked from the
 * capture-capsule spike, ADR-0006). Pure of platform: the one platform fact
 * the spike version read — React Native's __DEV__ — is injected by the caller
 * as a minLevel choice, which is what lets this run identically under Node,
 * the browser and Hermes, and lets the node suite test it.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return 'time';
  }
}

function stringifyMeta(meta: unknown) {
  if (meta == null) return '';
  try {
    if (typeof meta === 'string') return meta;
    return JSON.stringify(meta);
  } catch {
    return String(meta);
  }
}

/**
 * Mask a bearer credential for logging. Long tokens keep head+tail so two
 * masked values can still be told apart in a log; short ones mask entirely.
 */
export function maskToken(token?: string | null) {
  if (!token) return '';
  const t = String(token);
  if (t.length <= 14) return '***';
  return `${t.slice(0, 6)}…${t.slice(-4)}`;
}

export interface Logger {
  debug: (tag: string, message: string, meta?: unknown) => void;
  info: (tag: string, message: string, meta?: unknown) => void;
  warn: (tag: string, message: string, meta?: unknown) => void;
  error: (tag: string, message: string, meta?: unknown) => void;
  child: (tagPrefix: string) => Logger;
}

export type LogWriter = (level: LogLevel, line: string) => void;

/**
 * Structural, not `lib.dom`'s Console — same reasoning as the crypto
 * declaration in ids.ts: this package carries no DOM or Node lib, and every
 * target runtime (Node, browsers, Hermes) provides these three methods.
 */
declare const console: {
  log: (line: string) => void;
  warn: (line: string) => void;
  error: (line: string) => void;
};

const consoleWriter: LogWriter = (level, line) => {
  if (level === 'error') console.error(line);
  else if (level === 'warn') console.warn(line);
  else console.log(line);
};

export function createLogger(opts?: {
  /** Default 'warn': quiet in production unless the caller opts into more. */
  minLevel?: LogLevel;
  enabled?: boolean;
  /** Injectable sink, for tests and for future transport routing. */
  write?: LogWriter;
}): Logger {
  const minLevel: LogLevel = opts?.minLevel ?? 'warn';
  const enabled = opts?.enabled ?? true;
  const sink = opts?.write ?? consoleWriter;

  const shouldLog = (level: LogLevel) => enabled && LEVEL_ORDER[level] >= LEVEL_ORDER[minLevel];

  const write = (level: LogLevel, tag: string, message: string, meta?: unknown) => {
    if (!shouldLog(level)) return;
    const prefix = `[${nowIso()}][${level.toUpperCase()}][${tag}]`;
    const metaStr = stringifyMeta(meta);
    sink(level, metaStr ? `${prefix} ${message} ${metaStr}` : `${prefix} ${message}`);
  };

  const withPrefix = (tagPrefix: string | null): Logger => ({
    debug: (tag, message, meta) => write('debug', joinTag(tagPrefix, tag), message, meta),
    info: (tag, message, meta) => write('info', joinTag(tagPrefix, tag), message, meta),
    warn: (tag, message, meta) => write('warn', joinTag(tagPrefix, tag), message, meta),
    error: (tag, message, meta) => write('error', joinTag(tagPrefix, tag), message, meta),
    child: (next) => withPrefix(tagPrefix ? `${tagPrefix}:${next}` : next),
  });

  return withPrefix(null);
}

function joinTag(prefix: string | null, tag: string) {
  return prefix ? `${prefix}:${tag}` : tag;
}
